import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import equipmentService from '../../../services/equipmentService';

const EquipmentSelectionModal = ({ isOpen, onClose, onSelectEquipment }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchEquipment();
    }
  }, [isOpen]);

  const fetchEquipment = async () => {
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await equipmentService?.getAllEquipment();
    if (fetchError) {
      setError(fetchError);
      setEquipment([]);
    } else {
      setEquipment(data || []);
    }
    setLoading(false);
  };

  // Extract unique equipment types for filter
  const equipmentTypes = [
    { value: 'all', label: 'All Equipment Types' },
    ...Array.from(new Set(equipment?.map(eq => eq?.equipmentType)?.filter(Boolean)))
      ?.map(type => ({ value: type, label: type }))
  ];

  const filteredEquipment = equipment?.filter(eq => {
    const matchesSearch = eq?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         eq?.equipmentType?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesType = selectedType === 'all' || eq?.equipmentType === selectedType;
    
    return matchesSearch && matchesType;
  });

  const handleSelectEquipment = (equipment) => {
    if (onSelectEquipment) {
      onSelectEquipment({
        id: equipment?.id,
        name: equipment?.name,
        equipmentType: equipment?.equipmentType,
        powerRequirements: equipment?.powerRequirements || '0 kW',
        costPerUnit: equipment?.costPerUnit || 0,
        unitOfMeasure: equipment?.unitOfMeasure || 'hour',
        availabilityStatus: equipment?.availabilityStatus || 'available',
        availabilityCalendar: equipment?.availabilityCalendar || []
      });
    }
    onClose();
  };

  const handleReset = () => {
    setSearchQuery('');
    setSelectedType('all');
  };

  const getAvailabilityColor = (status) => {
    switch (status) {
      case 'available': return 'text-green-600';
      case 'in_use': return 'text-blue-600';
      case 'maintenance': return 'text-orange-600';
      case 'unavailable': return 'text-red-600';
      default: return 'text-muted-foreground';
    }
  };

  const getAvailabilityLabel = (status) => {
    switch (status) {
      case 'available': return 'Available';
      case 'in_use': return 'In Use';
      case 'maintenance': return 'Maintenance';
      case 'unavailable': return 'Unavailable';
      default: return 'Unknown';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-card rounded-xl shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h3 className="text-xl font-semibold text-foreground">Equipment Selection</h3>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-muted transition-colors duration-200"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 border-b border-border">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Search</label>
              <Input
                type="search"
                placeholder="Search equipment by name, type..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Equipment Type</label>
              <Select
                value={selectedType}
                onChange={(value) => setSelectedType(value)}
                options={equipmentTypes}
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Button variant="outline" onClick={handleReset}>
              <Icon name="RotateCcw" size={16} className="mr-2" />
              Reset Filters
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Icon name="Loader" size={48} color="var(--color-muted-foreground)" className="animate-spin" />
              <p className="mt-4 text-sm text-muted-foreground">Loading equipment...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Icon name="AlertCircle" size={48} color="var(--color-destructive)" />
              <p className="mt-4 text-sm text-destructive">Error loading equipment</p>
              <p className="text-xs text-muted-foreground">{error}</p>
            </div>
          ) : filteredEquipment?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Icon name="Wrench" size={48} color="var(--color-muted-foreground)" />
              <p className="mt-4 text-sm text-muted-foreground">No equipment found</p>
              <p className="text-xs text-muted-foreground">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              {filteredEquipment?.map((eq) => (
                <div
                  key={eq?.id}
                  className="flex items-start justify-between p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors duration-200 cursor-pointer"
                  onClick={() => handleSelectEquipment(eq)}
                >
                  <div className="flex-1">
                    <div className="flex items-start gap-3">
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 flex-shrink-0">
                        <Icon name="Wrench" size={20} color="var(--color-primary)" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-semibold text-foreground">{eq?.name}</h4>
                        <p className="text-xs text-muted-foreground">
                          {eq?.equipmentType || 'General Equipment'}
                        </p>
                        <div className="mt-2 flex items-center gap-4">
                          <div className="flex items-center gap-1">
                            <Icon name="Zap" size={14} color="var(--color-muted-foreground)" />
                            <span className="text-xs text-muted-foreground">{eq?.powerRequirements || '0 kW'}</span>
                          </div>
                          <div className={`flex items-center gap-1 ${getAvailabilityColor(eq?.availabilityStatus)}`}>
                            <Icon name="Circle" size={8} />
                            <span className="text-xs font-medium">{getAvailabilityLabel(eq?.availabilityStatus)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 ml-4">
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Cost per {eq?.unitOfMeasure || 'hour'}</p>
                      <p className="text-sm font-semibold text-foreground">
                        ${eq?.costPerUnit?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                    <Button size="sm" onClick={(e) => {
                      e?.stopPropagation();
                      handleSelectEquipment(eq);
                    }}>
                      Select
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-border flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EquipmentSelectionModal;